import { makeStyles } from '@fluentui/react-components';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';

import { MonthGrid, MonthSidebar } from '.';
import { ApplyDialog } from '../apply';
import { todayIso } from '../common';
import { DotSpinner } from '../components';
import { DayDialog } from '../day-dialog';
import { palette, tokens } from '../theme';
import { usePending } from '../worklog';
import { type DayCell, useCurrentMonth, useMonthCells, useMonthStats } from './index';

const BackfillDialog = lazy(() => import('../backfill').then((m) => ({ default: m.BackfillDialog })));

export const MonthView: React.FC = () => {
    const classes = useStyles();
    const { pending, clear: clearPending } = usePending();
    const { current: currentMonth, set: setCurrentMonth } = useCurrentMonth();

    const [openCell, setOpenCell] = useState<DayCell | null>(null);
    const [backfillOpen, setBackfillOpen] = useState(false);
    const [applyOpen, setApplyOpen] = useState(false);
    const todayDate = useMemo(() => todayIso(), []);

    const { state: cellsState } = useMonthCells();

    const handleDayClick = useCallback((cell: DayCell) => setOpenCell(cell), []);
    const closeDialog = useCallback(() => setOpenCell(null), []);
    const openBackfill = useCallback(() => setBackfillOpen(true), []);
    const closeBackfill = useCallback(() => setBackfillOpen(false), []);
    const openApply = useCallback(() => setApplyOpen(true), []);
    const closeApply = useCallback(() => setApplyOpen(false), []);

    const liveCell = useMemo(() => {
        if (!openCell || cellsState.status !== 'success') {
            return openCell;
        }
        return cellsState.data.find((c) => c.date === openCell.date) ?? openCell;
    }, [openCell, cellsState]);

    const { stats, pendingForMonth, legendCounts } = useMonthStats(cellsState, pending, currentMonth);

    return (
        <section className={classes.root}>
            <div className={classes.body}>
                <div className={classes.calendarColumn}>
                    {cellsState.status === 'loading' && (
                        <div className={classes.placeholder}>
                            <DotSpinner label="Loading month" />
                        </div>
                    )}
                    {cellsState.status === 'error' && <div className={classes.error}>Failed to load month: {cellsState.error.message}</div>}
                    {cellsState.status === 'success' && <MonthGrid cells={cellsState.data} today={todayDate} onDayClick={handleDayClick} />}
                </div>

                <div className={classes.rightColumn}>
                    <MonthSidebar
                        stats={stats}
                        pendingCount={pendingForMonth.length}
                        legendCounts={legendCounts}
                        onBackfill={openBackfill}
                        onApply={openApply}
                        onResetPending={clearPending}
                    />
                </div>
            </div>

            {liveCell && <DayDialog cell={liveCell} onClose={closeDialog} />}

            {backfillOpen && cellsState.status === 'success' && (
                <Suspense fallback={null}>
                    <BackfillDialog
                        year={currentMonth.year}
                        month={currentMonth.month}
                        cells={cellsState.data}
                        onClose={closeBackfill}
                        onMonthChange={(y, m) => setCurrentMonth({ year: y, month: m })}
                    />
                </Suspense>
            )}

            {applyOpen && <ApplyDialog onClose={closeApply} />}
        </section>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        gap: tokens.spacingVerticalS,
        minHeight: 0,
    },
    body: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        flexGrow: 1,
        minHeight: 0,
    },
    calendarColumn: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minWidth: 0,
        gap: tokens.spacingVerticalS,
    },
    rightColumn: {
        display: 'flex',
        flexDirection: 'column',
        width: '320px',
        flexShrink: 0,
    },
    placeholder: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: tokens.spacingVerticalM,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    error: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: tokens.fontSizeBase300,
        color: palette.todayAccent,
        textAlign: 'center',
    },
});
